import { AnalysisFeeCalculatorDTO } from "@models/platform-analysis/entities/steps/fee";
import { AnalysisVariable } from "@models/platform-analysis/enums/variableName";
import { RootState } from "@redux/store";
import { useEffect, useMemo } from "react";
import { FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";

export const useFeeCalculatorInputField = (
  variableName: AnalysisVariable = AnalysisVariable.None,
  linkedFieldVariableNames?: AnalysisVariable[],
) => {
  const formMethods = useFormContext<AnalysisFeeCalculatorDTO>();
  const pathsMap = useSelector(
    (state: RootState) =>
      state.platformAnalysis?.internal?.feeCalculator?.feeCalculatorPathsMap,
  );

  const calculatorPath = pathsMap[variableName]
    ?.feeCalculatorPath as FieldPath<AnalysisFeeCalculatorDTO>;
  const fieldPath = pathsMap[variableName]
    ?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>;
  const fieldName = pathsMap[variableName]?.databaseName || "";

  const linkedFieldPaths = useMemo(() => {
    if (!linkedFieldVariableNames || linkedFieldVariableNames.length === 0) {
      return [];
    }

    return linkedFieldVariableNames.map(
      (variable) =>
        pathsMap[variable]
          ?.dataValuePath as FieldPath<AnalysisFeeCalculatorDTO>,
    );
  }, [linkedFieldVariableNames, pathsMap]);

  const fieldValue = useWatch({
    control: formMethods.control,
    name: fieldPath,
  });

  useEffect(() => {
    if (linkedFieldPaths.length === 0) return;
    linkedFieldPaths.forEach((linkedPath) => {
      const currentValue = formMethods.getValues(linkedPath);
      const newValue = String(fieldValue);

      if (currentValue === newValue) return;
      formMethods.setValue(linkedPath, newValue, {
        shouldDirty: false,
        shouldTouch: false,
        shouldValidate: false,
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldValue]);

  return {
    formMethods,
    fieldPath,
    calculatorPath,
    fieldName,
    linkedFieldPaths,
  };
};
